// Hayalini Anlat design preferences: shared by the form, the API validation and the AI prompts.
// Every option is optional; "auto" (or empty) lets the AI decide.

type Option = { id: string; tr: string; en: string; prompt: string }

export const PROJECT_TYPES: Option[] = [
  { id: 'website', tr: 'Web Sitesi', en: 'Website', prompt: 'a website (corporate or showcase)' },
  { id: 'web-app', tr: 'Web Yazılım / Panel', en: 'Web App / Dashboard', prompt: 'a web application or dashboard' },
  { id: 'mobile', tr: 'Mobil Uygulama', en: 'Mobile App', prompt: 'a mobile app (iOS and Android)' },
  { id: 'ecommerce', tr: 'E-ticaret', en: 'E-commerce', prompt: 'an e-commerce store' },
  { id: 'automation', tr: 'Otomasyon / Yapay Zeka', en: 'Automation / AI', prompt: 'an automation or AI tool with a dashboard' },
  { id: 'desktop', tr: 'Masaüstü Uygulama', en: 'Desktop App', prompt: 'a desktop application' },
]

export const STYLES: Option[] = [
  { id: 'minimal', tr: 'Modern Minimalist', en: 'Modern Minimalist', prompt: 'modern minimalist: generous white space, few colours, clean lines' },
  { id: 'corporate', tr: 'Kurumsal & Güvenilir', en: 'Corporate & Trustworthy', prompt: 'corporate and trustworthy: structured grid, calm colours, professional imagery' },
  { id: 'luxury', tr: 'Lüks & Premium', en: 'Luxury & Premium', prompt: 'luxury premium: dark or cream backgrounds, elegant details, refined photography' },
  { id: 'playful', tr: 'Eğlenceli & Renkli', en: 'Playful & Colourful', prompt: 'playful and colourful: rounded shapes, bright accents, friendly illustrations' },
  { id: 'tech', tr: 'Teknolojik & Fütüristik', en: 'Tech & Futuristic', prompt: 'tech futuristic: dark mode, neon accents, glassmorphism, data visuals' },
  { id: 'natural', tr: 'Doğal & Organik', en: 'Natural & Organic', prompt: 'natural organic: earthy tones, soft textures, nature photography' },
  { id: 'bold', tr: 'Cesur & Dikkat Çekici', en: 'Bold & Striking', prompt: 'bold and striking: big typography, strong contrast, vivid blocks of colour' },
  { id: 'retro', tr: 'Retro & Nostaljik', en: 'Retro & Nostalgic', prompt: 'retro nostalgic: vintage colours, classic badges, warm grain' },
]

export const FONTS: (Option & { css: string })[] = [
  { id: 'sans', tr: 'Modern Sans', en: 'Modern Sans', prompt: 'clean modern sans-serif (like Inter)', css: 'ui-sans-serif, system-ui, "Segoe UI", Arial, sans-serif' },
  { id: 'geometric', tr: 'Geometrik', en: 'Geometric', prompt: 'geometric sans-serif (like Montserrat or Poppins)', css: '"Century Gothic", "Avenir Next", "Futura", sans-serif' },
  { id: 'serif', tr: 'Zarif Serif', en: 'Elegant Serif', prompt: 'elegant high-contrast serif headings (like Playfair Display)', css: 'Georgia, "Times New Roman", serif' },
  { id: 'rounded', tr: 'Yuvarlak & Samimi', en: 'Rounded & Friendly', prompt: 'rounded friendly sans-serif (like Nunito)', css: 'ui-rounded, "Arial Rounded MT Bold", "Trebuchet MS", sans-serif' },
  { id: 'mono', tr: 'Teknik / Mono', en: 'Technical / Mono', prompt: 'technical monospace accents (like JetBrains Mono)', css: 'var(--font-jetbrains-mono), ui-monospace, monospace' },
  { id: 'display', tr: 'Güçlü Başlık', en: 'Bold Display', prompt: 'heavy condensed display headings (like Bebas Neue)', css: 'Impact, "Arial Narrow Bold", sans-serif' },
]

export const PALETTES: { id: string; tr: string; en: string; colors: string[] }[] = [
  { id: 'ocean', tr: 'Okyanus', en: 'Ocean', colors: ['#034AA6', '#38BDF8', '#F8FAFC'] },
  { id: 'forest', tr: 'Orman', en: 'Forest', colors: ['#14532D', '#84CC16', '#FEFCE8'] },
  { id: 'sunset', tr: 'Gün Batımı', en: 'Sunset', colors: ['#EA580C', '#F43F5E', '#FFF7ED'] },
  { id: 'gold', tr: 'Siyah & Altın', en: 'Black & Gold', colors: ['#111111', '#C9A227', '#F5F0E6'] },
  { id: 'lavender', tr: 'Lavanta', en: 'Lavender', colors: ['#6D28D9', '#C4B5FD', '#FAF5FF'] },
  { id: 'earth', tr: 'Toprak', en: 'Earth', colors: ['#7C4A2D', '#D4A373', '#FAEDCD'] },
  { id: 'mint', tr: 'Nane', en: 'Mint', colors: ['#0F766E', '#5EEAD4', '#F0FDFA'] },
  { id: 'mono', tr: 'Siyah & Beyaz', en: 'Black & White', colors: ['#0A0A0A', '#737373', '#FFFFFF'] },
]

export type DesignPreferences = {
  projectType?: string
  brandName?: string
  style?: string
  colors?: string[]
  font?: string
}

const HEX = /^#[0-9a-f]{6}$/i
const ids = (list: { id: string }[]) => new Set(list.map((o) => o.id))
const TYPE_IDS = ids(PROJECT_TYPES)
const STYLE_IDS = ids(STYLES)
const FONT_IDS = ids(FONTS)

/** Keep only known option ids, a short plain brand name and up to 4 hex colours. */
export function sanitizeDesign(input: unknown): DesignPreferences | null {
  if (!input || typeof input !== 'object') return null
  const d = input as Record<string, unknown>
  const out: DesignPreferences = {}
  if (typeof d.projectType === 'string' && TYPE_IDS.has(d.projectType)) out.projectType = d.projectType
  if (typeof d.style === 'string' && STYLE_IDS.has(d.style)) out.style = d.style
  if (typeof d.font === 'string' && FONT_IDS.has(d.font)) out.font = d.font
  if (typeof d.brandName === 'string') {
    const name = d.brandName.replace(/[<>"`{}\\]/g, '').replace(/\s+/g, ' ').trim().slice(0, 40)
    if (name) out.brandName = name
  }
  if (Array.isArray(d.colors)) {
    const colors = d.colors.filter((c): c is string => typeof c === 'string' && HEX.test(c)).slice(0, 4)
    if (colors.length) out.colors = colors.map((c) => c.toUpperCase())
  }
  return Object.keys(out).length ? out : null
}

const find = (list: Option[], id?: string) => list.find((o) => o.id === id)

/** Human-readable lines (for e-mails and the share page). */
export function describeDesign(d: DesignPreferences, locale: 'tr' | 'en'): { label: string; value: string }[] {
  const L = locale === 'en'
  const rows: { label: string; value: string }[] = []
  const type = find(PROJECT_TYPES, d.projectType)
  if (type) rows.push({ label: L ? 'Project type' : 'Proje türü', value: type[locale] })
  if (d.brandName) rows.push({ label: L ? 'Brand name' : 'Marka adı', value: d.brandName })
  const style = find(STYLES, d.style)
  if (style) rows.push({ label: L ? 'Style' : 'Stil', value: style[locale] })
  if (d.colors?.length) rows.push({ label: L ? 'Colours' : 'Renkler', value: d.colors.join(', ') })
  const font = find(FONTS, d.font)
  if (font) rows.push({ label: L ? 'Typography' : 'Yazı tipi', value: font[locale] })
  return rows
}

/** English instructions for the AI prompts; empty when nothing was chosen. */
export function designPromptText(d: DesignPreferences | null): string {
  if (!d) return ''
  const lines: string[] = []
  const type = find(PROJECT_TYPES, d.projectType)
  if (type) lines.push(`- Project type: ${type.prompt} (choose the device scene for this type)`)
  if (d.brandName) lines.push(`- Brand name: "${d.brandName}" — use exactly this name as the logo/brand in the UI`)
  const style = find(STYLES, d.style)
  if (style) lines.push(`- Visual style: ${style.prompt}`)
  if (d.colors?.length) lines.push(`- Colour palette: build the UI mainly with ${d.colors.join(', ')} (first = primary)`)
  const font = find(FONTS, d.font)
  if (font) lines.push(`- Typography: ${font.prompt}`)
  return lines.length ? `Client's design preferences (MUST be followed, they override your own choices):\n${lines.join('\n')}` : ''
}
